import { useQuery, useQueryClient } from '@tanstack/react-query'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import StreamExpensePolicyAgentChat from 'src/agent/components/StreamExpensePolicyAgentChat/StreamExpensePolicyAgentChat'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { hasDisplayablePolicyAgentInsight } from 'src/app/TransactionDetail/policyAgentInsights'
import { shouldRenderReceiptAgentManagerReview } from 'src/app/TransactionDetail/receiptAgentFlyoutVisibility'
import { getReceiptEvidenceAttachment } from 'src/app/TransactionDetail/receiptEvidence'
import ReceiptEvidencePanel from 'src/app/TransactionDetail/ReceiptEvidencePanel'
import {
  getLocalReceiptEvidenceAttachmentPreviewType,
  getLocalReceiptRecoveryPreviewState,
  getReceiptRecoveryCaseForDisplay,
} from 'src/app/TransactionDetail/receiptRecoveryPreview'
import {
  selectFailedUploadFileNamesFromState,
  selectFailedUploadThumbnailsFromState,
  selectRecentlyUploadedAttachmentIdsFromState,
  selectTransactionAttachmentIdFromState,
  selectTransactionIdFromPathState,
  selectUploadingAttachmentFileNamesFromState,
  selectUploadingAttachmentsFromState,
} from 'src/app/TransactionDetail/redux/transactionDetail.selectors'
import AdditionalFieldsSection from 'src/app/TransactionDetail/Sections/AdditionalFieldsSection'
import CardDetailsSection from 'src/app/TransactionDetail/Sections/CardDetailsSection'
import ExpenseDetailsSection from 'src/app/TransactionDetail/Sections/ExpenseDetailsSection'
import ManagerReviewSection from 'src/app/TransactionDetail/Sections/ManagerReviewSection'
import ReceiptsSection from 'src/app/TransactionDetail/Sections/ReceiptsSection'
import TransactionSummarySection, {
  TransactionSummaryHeader,
} from 'src/app/TransactionDetail/Sections/TransactionSummarySection'
import TransactionDetailTabs from 'src/component-layout/TransactionDetailTabs/TransactionDetailTabs'
import { useAIPolicyAssistantEnabled } from 'src/components/ExpensePolicyUploadBox/expensePolicy.queries'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import { TransactionExpenseCategorySplit } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import TransactionActivitiesTab from 'src/feature/TransactionActivities/TransactionActivitiesTab'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  ExpenseDetailsModel,
  TransactionAiInsightType,
  TransactionDetailModel,
  TransactionDetailResponse,
} from 'src/models/transaction.models'
import { closeModal } from 'src/navigation/Lnk'
import { useQueryCreditCardPermissions } from 'src/queries/creditCard.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryResponse,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { orgReceiptRulesQueries } from 'src/queries/organizations/receiptRules/receiptRules.queries'
import {
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentSuccessAction,
} from 'src/queries/receiptAttachments/receiptAttachments.events'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import {
  updateTransactionDetailResponse,
  usePatchTransaction,
  useQueryTransactionDetail,
  useQueryTransactionsPerson,
  useSplitTransactionExpenseData,
  useUnsplitTransactionExpenseData,
} from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { useQueryVirtualCardDetail } from 'src/queries/virtualCards.queries'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import { selectIsAttachmentPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { UpdateTransactionExpenseDetailRequest } from 'src/requests/updateTransactionExpenseDetails.request'
import { toCents, toDollars } from 'src/utils/currency.utils'
import {
  isExpensePolicyAIChatEnabled,
  isExpensePolicyAIEnabled,
  isTransactionConversationsEnabled,
} from 'src/utils/featureFlags'

import style from './TransactionDetail.style'
import {
  getTransactionDetailTabState,
  TransactionDetailTabState,
} from './transactionDetailTabState'
import { useReceiptModalRoute } from './useReceiptModalRoute'

const normalizeLabelId = (
  labelId?: string,
  labelName?: string,
): string | null => {
  const candidate = labelId ?? labelName
  if (candidate === undefined || candidate === null) return null
  const trimmed = String(candidate).trim()
  return trimmed === '' ? null : trimmed
}

const resolveExpenseCategoryId = (
  detail: ExpenseDetailsModel,
  expCatSchema?: ExpenseCategoryResponse[],
): string | undefined => {
  if (
    detail.categoryType === ExpenseCategoryType.Customer ||
    detail.categoryType === ExpenseCategoryType.Vendor
  ) {
    const schemaCategory = expCatSchema?.find(
      (category) => category.type === detail.categoryType,
    )
    return schemaCategory?.id ?? detail.categoryId
  }

  return detail.categoryId
}

const buildExpenseCategoriesFromDetails = (
  expenseDetails: ExpenseDetailsModel[],
  expCatSchema?: ExpenseCategoryResponse[],
): TransactionExpenseCategorySplit['expenseCategories'] => {
  return expenseDetails.reduce(
    (acc, detail) => {
      const resolvedCategoryId = resolveExpenseCategoryId(detail, expCatSchema)
      if (!resolvedCategoryId) {
        return acc
      }
      acc[resolvedCategoryId] = {
        categoryId: resolvedCategoryId,
        labelId: detail.labelId,
        labelName: detail.labelName,
      }
      return acc
    },
    {} as TransactionExpenseCategorySplit['expenseCategories'],
  )
}

export const buildSplitExpenseCategoriesFromDetails = ({
  expenseDetails,
  totalAmountCents,
  isSplit,
  expCatSchema,
  shareDepartmentAcrossSplits,
}: {
  expenseDetails?: ExpenseDetailsModel[]
  totalAmountCents: number
  isSplit?: boolean
  expCatSchema?: ExpenseCategoryResponse[]
  shareDepartmentAcrossSplits?: boolean
}): TransactionExpenseCategorySplit[] => {
  const formatSplitValue = (cents: number): string => {
    return toDollars(cents).toFixed(2)
  }

  if (!expenseDetails || expenseDetails.length === 0) {
    return [
      {
        splitValue: formatSplitValue(totalAmountCents),
        expenseCategories: {},
      },
    ]
  }

  const eligibleExpenseDetails = expenseDetails
  const departmentCategoryId = shareDepartmentAcrossSplits
    ? expCatSchema?.find((category) => category.code === 'tc_DEPARTMENTS')?.id
    : undefined
  const globalSharedDetails = expenseDetails.filter(
    (detail) =>
      (detail.splitIndex === null || detail.splitIndex === undefined) &&
      (detail.categoryType === ExpenseCategoryType.Customer ||
        detail.categoryType === ExpenseCategoryType.Vendor ||
        (departmentCategoryId !== undefined &&
          detail.categoryId === departmentCategoryId)),
  )
  const globalSharedMappings =
    globalSharedDetails.length > 0
      ? buildExpenseCategoriesFromDetails(globalSharedDetails, expCatSchema)
      : {}
  const hasGlobalSharedMappings = Object.keys(globalSharedMappings).length > 0
  const mergeGlobalMappings = (
    split: TransactionExpenseCategorySplit,
  ): TransactionExpenseCategorySplit => {
    if (!hasGlobalSharedMappings) {
      return split
    }
    return {
      ...split,
      expenseCategories: {
        ...globalSharedMappings,
        ...(split.expenseCategories ?? {}),
      },
    }
  }
  const hasSplitData =
    isSplit ||
    expenseDetails.some(
      (detail) => detail.splitIndex !== null && detail.splitIndex !== undefined,
    )

  if (!hasSplitData) {
    return [
      mergeGlobalMappings({
        splitValue: formatSplitValue(totalAmountCents),
        expenseCategories: buildExpenseCategoriesFromDetails(
          eligibleExpenseDetails,
          expCatSchema,
        ),
      }),
    ]
  }

  const grouped = new Map<number, ExpenseDetailsModel[]>()
  expenseDetails.forEach((detail) => {
    if (detail.splitIndex === null || detail.splitIndex === undefined) {
      return
    }
    const next = grouped.get(detail.splitIndex) ?? []
    next.push(detail)
    grouped.set(detail.splitIndex, next)
  })

  const splitIndices = Array.from(grouped.keys()).sort((a, b) => a - b)
  const splits = splitIndices.map((splitIndex) => {
    const group = grouped.get(splitIndex) ?? []
    const eligibleGroup = group
    const splitAmountCents = group.find(
      (detail) =>
        detail.splitAmountCents !== null &&
        detail.splitAmountCents !== undefined,
    )?.splitAmountCents

    return {
      splitValue:
        splitAmountCents !== undefined && splitAmountCents !== null
          ? formatSplitValue(splitAmountCents)
          : '',
      expenseCategories: buildExpenseCategoriesFromDetails(
        eligibleGroup,
        expCatSchema,
      ),
    }
  })

  if (splits.length === 0) {
    return [
      mergeGlobalMappings({
        splitValue: formatSplitValue(totalAmountCents),
        expenseCategories: buildExpenseCategoriesFromDetails(
          eligibleExpenseDetails,
          expCatSchema,
        ),
      }),
    ]
  }

  return splits.map((split) => mergeGlobalMappings(split))
}

const buildSplitExpenseDetailRequests = (
  splits: TransactionExpenseCategorySplit[],
): UpdateTransactionExpenseDetailRequest[] => {
  return splits.flatMap((split, splitIndex) => {
    const splitValue =
      typeof split.splitValue === 'number'
        ? split.splitValue
        : Number.parseFloat(String(split.splitValue ?? 0))
    const splitAmountCents = toCents(
      Number.isFinite(splitValue) ? splitValue : 0,
    )

    return Object.values(split.expenseCategories ?? {}).flatMap((mapping) => {
      const labelId = normalizeLabelId(mapping.labelId, mapping.labelName)
      if (labelId === null) return []
      return [
        {
          categoryId: mapping.categoryId,
          labelId,
          splitIndex,
          splitAmountCents,
        },
      ]
    })
  })
}

const buildUnsplitExpenseDetailRequests = (
  expenseDetails?: ExpenseDetailsModel[],
  expCatSchema?: ExpenseCategoryResponse[],
): UpdateTransactionExpenseDetailRequest[] => {
  if (!expenseDetails || expenseDetails.length === 0) {
    return []
  }

  const sorted = [...expenseDetails].sort(
    (a, b) => (a.splitIndex ?? 0) - (b.splitIndex ?? 0),
  )
  const seen = new Set<string>()

  return sorted.reduce((acc, detail) => {
    const resolvedCategoryId = resolveExpenseCategoryId(detail, expCatSchema)
    if (!resolvedCategoryId || seen.has(resolvedCategoryId)) return acc
    seen.add(resolvedCategoryId)
    const labelId = normalizeLabelId(detail.labelId, detail.labelName)
    if (labelId === null) return acc
    acc.push({
      categoryId: resolvedCategoryId,
      labelId,
    })
    return acc
  }, [] as UpdateTransactionExpenseDetailRequest[])
}

const TransactionDetail: React.FC = () => {
  const translate = useTranslate()
  const dispatch = useFlyoutDispatch()
  const queryClient = useQueryClient()
  const uploadReceiptAttachments = useUploadReceiptAttachments()
  const transactionId = useSelector(selectTransactionIdFromPathState)
  const [transactionDetailTabState, setTransactionDetailTabState] =
    useState<TransactionDetailTabState>({
      transactionId,
      activeTab: 'overview',
    })

  useEffect(() => {
    setTransactionDetailTabState((current) =>
      getTransactionDetailTabState(current, transactionId),
    )
  }, [transactionId])

  const attachmentId = useSelector(selectTransactionAttachmentIdFromState)
  const { data: transactionData } = useQueryTransactionDetail({
    transactionId,
  })
  const transaction = useMemo(
    () =>
      transactionData instanceof TransactionDetailModel
        ? transactionData
        : transactionData
          ? new TransactionDetailModel(transactionData)
          : undefined,
    [transactionData],
  )
  const isAttachmentPopupOpen = useSelector(
    selectIsAttachmentPopupOpenFromState,
  )
  const receiptModal = useReceiptModalRoute(
    transaction?.receiptAttachments ?? [],
    isAttachmentPopupOpen,
  )

  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const shareDepartmentAcrossSplits = useMemo(
    () =>
      [
        'QUICKBOOKS_ONLINE',
        'QUICKBOOKS_DESKTOP',
        'QUICKBOOKS_ONLINE_SANDBOX',
      ].includes(activeIntegration?.connectedPlatform ?? ''),
    [activeIntegration?.connectedPlatform],
  )

  const { data: virtualCard } = useQueryVirtualCardDetail({
    virtualCardId: transaction?.virtualCardId,
  })
  const { data: user } = useQueryOwnUser({})
  const { data: cardUser } = useQueryTransactionsPerson(
    transaction?.recipientId,
  )
  const { data: cardCreator } = useQueryTransactionsPerson(
    virtualCard?.cardholderId,
  )
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: transaction?.creditCardId,
    retry: false,
  })
  const { data: parentCreditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: transaction?.parentCreditCardId,
    retry: false,
  })
  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })
  // const { data: creditCard } = useQueryCreditCard({ creditCardId: transaction?.creditCardId, enabled: creditCardPermissions !== undefined && creditCardPermissions.permissions.length > 0 })

  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const uploadingAttachmentFileNames = useSelector(
    selectUploadingAttachmentFileNamesFromState,
  )
  const failedUploadThumbnails = useSelector(
    selectFailedUploadThumbnailsFromState,
  )
  const failedUploadFileNames = useSelector(
    selectFailedUploadFileNamesFromState,
  )
  const recentlyUploadedAttachmentIds = useSelector(
    selectRecentlyUploadedAttachmentIdsFromState,
  )
  const { analytics } = useAnalyticsContext()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(organization?.id),
  )
  const orgReceiptRulesQuery = useQuery({
    ...orgReceiptRulesQueries.detail(organization?.id),
    enabled: Boolean(organization?.id) && receiptManagementEnabled,
  })

  const { mutate: patchTransaction, error: attachmentsTopLevelError } =
    usePatchTransaction()
  const {
    mutateAsync: splitTransactionExpenseData,
    error: splitTransactionExpenseDataError,
  } = useSplitTransactionExpenseData()
  const { mutateAsync: unsplitTransactionExpenseData } =
    useUnsplitTransactionExpenseData()

  const [splitExpenseCategories, setSplitExpenseCategories] = useState<
    TransactionExpenseCategorySplit[]
  >([])
  const expenseDetailsSignature = useMemo(() => {
    if (!transaction?.expenseDetails || transaction.expenseDetails.length === 0)
      return ''

    return transaction.expenseDetails
      .map((detail) => {
        return [
          detail.categoryId ?? '',
          detail.categoryType ?? '',
          detail.labelId ?? '',
          detail.labelName ?? '',
          detail.splitIndex ?? '',
          detail.splitAmountCents ?? '',
        ].join(':')
      })
      .sort()
      .join('|')
  }, [transaction?.expenseDetails])
  const handleSaveSplitExpenseCategories = useCallback(
    async (
      splits: TransactionExpenseCategorySplit[],
    ): Promise<TransactionDetailResponse | void> => {
      if (!transactionId) return

      if (splits.length === 0) {
        if (!transaction?.isSplit) {
          setSplitExpenseCategories(
            buildSplitExpenseCategoriesFromDetails({
              expenseDetails: transaction?.expenseDetails,
              totalAmountCents: transaction?.clearingOrAuthAmountCents ?? 0,
              isSplit: false,
              expCatSchema,
              shareDepartmentAcrossSplits,
            }),
          )
          return
        }

        const response = await unsplitTransactionExpenseData({
          transactionId,
          body: {
            expenseDetails: buildUnsplitExpenseDetailRequests(
              transaction?.expenseDetails,
              expCatSchema,
            ),
          },
        })

        setSplitExpenseCategories(
          buildSplitExpenseCategoriesFromDetails({
            expenseDetails: response.expenseDetails,
            totalAmountCents: transaction?.clearingOrAuthAmountCents ?? 0,
            isSplit: response.isSplit,
            expCatSchema,
            shareDepartmentAcrossSplits,
          }),
        )
        return response
      }

      const response = await splitTransactionExpenseData({
        transactionId,
        body: {
          expenseDetails: buildSplitExpenseDetailRequests(splits),
        },
      })

      setSplitExpenseCategories(
        buildSplitExpenseCategoriesFromDetails({
          expenseDetails: response.expenseDetails,
          totalAmountCents: transaction?.clearingOrAuthAmountCents ?? 0,
          isSplit: response.isSplit,
          expCatSchema,
          shareDepartmentAcrossSplits,
        }),
      )
      return response
    },
    [
      expCatSchema,
      shareDepartmentAcrossSplits,
      transaction?.clearingOrAuthAmountCents,
      transaction?.expenseDetails,
      transaction?.isSplit,
      transactionId,
      unsplitTransactionExpenseData,
      splitTransactionExpenseData,
    ],
  )
  const [openInitialAttachment, setOpenInitialAttachment] =
    useState<boolean>(true)

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutateAsync(attachments).then((response) => {
      const successfulResponses = response.filter(
        (resp) => resp.type === UPLOAD_ATTACHMENT_SUCCESS,
      ) as UploadAttachmentSuccessAction[]

      if (successfulResponses.length === 0) return

      // Optimistic cache update to keep the image thumbnail visible between upload and txn save; reconciled on response.
      updateTransactionDetailResponse(queryClient, {
        transactionId,
        updater: (old: TransactionDetailResponse) => {
          const existingIds = new Set(old.receiptAttachments.map((ra) => ra.id))
          const newReceiptAttachments: TransactionDetailResponse['receiptAttachments'] =
            []

          for (const successful of successfulResponses) {
            const receiptAttachment = successful.payload
            if (!existingIds.has(receiptAttachment.id)) {
              existingIds.add(receiptAttachment.id)
              newReceiptAttachments.push(receiptAttachment)
            }
          }

          if (newReceiptAttachments.length === 0) return old

          return {
            ...old,
            receiptAttachments: [
              ...newReceiptAttachments,
              ...old.receiptAttachments,
            ],
          }
        },
      })

      const existingAttachmentIds =
        transaction?.receiptAttachments.map((ra) => ra.id) || []
      const newAttachmentIds = successfulResponses.map(
        (successful) => successful.payload.id,
      )
      const newAttachmentIdsSet = new Set(newAttachmentIds)

      patchTransaction({
        transactionId,
        body: {
          receiptAttachmentIds: [
            ...newAttachmentIds,
            ...existingAttachmentIds.filter(
              (id) => !newAttachmentIdsSet.has(id),
            ),
          ],
        },
      })
    })
    analytics?.track('transaction detail - upload attachment - button press', {
      merchant: transaction?.merchantName,
      merchantCategory:
        transaction?.merchantName !== undefined
          ? transaction?.mccGroup
          : undefined,
      transactionDate: transaction
        ?.clearingOrAuthDateMoment(false)
        ?.format('l'),
      clearedDate: transaction?.clearingOrAuthDateMoment(true)?.format('L'),
      card: transaction?.vcnDisplayName,
      totalNumberOfAttachments: attachments.length,
      transactionId: transactionId,
      status: transaction?.status,
    })
  }

  const dispatchPatchTransaction = (
    body: Partial<PatchTransactionRequest>,
  ): void => {
    patchTransaction({ transactionId, body })
  }

  const deleteAttachment = useCallback(
    (attachmentId: string): void => {
      const newAttachments = transaction?.receiptAttachments.filter(
        (tr) => tr.id !== attachmentId,
      )
      patchTransaction({
        transactionId,
        body: {
          receiptAttachmentIds: newAttachments?.map((tr) => tr.id),
        },
      })
    },
    [patchTransaction, transaction?.receiptAttachments, transactionId],
  )

  useEffect(() => {
    if (!transaction) return
    setSplitExpenseCategories(
      buildSplitExpenseCategoriesFromDetails({
        expenseDetails: transaction.expenseDetails,
        totalAmountCents: transaction.clearingOrAuthAmountCents ?? 0,
        isSplit: transaction.isSplit,
        expCatSchema,
        shareDepartmentAcrossSplits,
      }),
    )
  }, [
    expCatSchema,
    expenseDetailsSignature,
    shareDepartmentAcrossSplits,
    transaction?.id,
    transaction?.isSplit,
    transaction?.updatedAt,
  ])

  useEffect((): void => {
    const attachment = transaction?.receiptAttachments.find((attachment) => {
      return attachment.id === attachmentId
    })

    if (openInitialAttachment && attachment) {
      dispatch(
        createSetIsAttachmentPopupOpen(
          true,
          (): void => {
            deleteAttachment(attachment.id)
          },
          attachment,
        ),
      )
      setOpenInitialAttachment(false)
    }
  }, [attachmentId, transaction?.receiptAttachments])

  useEffect(() => {
    const attachment = receiptModal.attachment
    if (!receiptModal.isOpen || attachment === undefined) return

    dispatch(
      createSetIsAttachmentPopupOpen(
        true,
        (): void => {
          deleteAttachment(attachment.id)
        },
        attachment,
      ),
    )

    return () => {
      dispatch(createSetIsAttachmentPopupOpen(false))
    }
  }, [deleteAttachment, dispatch, receiptModal.attachment, receiptModal.isOpen])

  const featureFlags = useSelector(selectFeatureFlaggerFromState)
  const showExpensePolicyAI = isExpensePolicyAIEnabled(
    featureFlags,
    user?.organizationId,
  )
  const showExpensePolicyAIChat = isExpensePolicyAIChatEnabled(featureFlags)
  const transactionActivitiesEnabled =
    featureFlags.enable_transaction_activities
  const receiptAgentFlyoutEnabled =
    featureFlags.enable_receipt_agent_transaction_flyout
  const receiptRecoveryMockDataEnabled =
    featureFlags.enable_receipt_recovery_mock_data
  const environment = useSelector(selectEnvironmentFromState)
  const activeTab =
    transactionActivitiesEnabled &&
    transactionDetailTabState.transactionId === transactionId
      ? transactionDetailTabState.activeTab
      : 'overview'

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const aiPolicyAssistantEnabled = useAIPolicyAssistantEnabled()
  const hasPolicyInsights = useMemo(
    () =>
      transaction?.transactionAiInsights?.find(
        (insight) =>
          insight.insightType === TransactionAiInsightType.PolicyAgent,
      ),
    [transaction?.transactionAiInsights],
  )
  const hasDisplayablePolicyInsights = useMemo(
    () => hasDisplayablePolicyAgentInsight(transaction?.transactionAiInsights),
    [transaction?.transactionAiInsights],
  )
  const receiptRecoveryCase = useMemo(
    () =>
      getReceiptRecoveryCaseForDisplay({
        enabled: receiptAgentFlyoutEnabled,
        mockDataEnabled: receiptRecoveryMockDataEnabled,
        receiptRecoveryCase: transaction?.receiptRecoveryCase,
        localPreviewState: getLocalReceiptRecoveryPreviewState({
          enabled: receiptAgentFlyoutEnabled,
          mockDataEnabled: receiptRecoveryMockDataEnabled,
          environment,
          search: typeof window === 'undefined' ? '' : window.location.search,
        }),
      }),
    [
      environment,
      receiptAgentFlyoutEnabled,
      receiptRecoveryMockDataEnabled,
      transaction?.receiptRecoveryCase,
    ],
  )
  const isMockReceiptRecoveryPreview =
    receiptAgentFlyoutEnabled &&
    receiptRecoveryMockDataEnabled &&
    transaction?.receiptRecoveryCase === undefined &&
    receiptRecoveryCase !== undefined
  const localReceiptEvidenceAttachmentPreviewType =
    !transaction?.receiptRecoveryCase
      ? getLocalReceiptEvidenceAttachmentPreviewType({
          enabled: receiptAgentFlyoutEnabled,
          mockDataEnabled: receiptRecoveryMockDataEnabled,
          environment,
          search: typeof window === 'undefined' ? '' : window.location.search,
        })
      : undefined
  const receiptEvidenceAttachment = getReceiptEvidenceAttachment({
    receiptRecoveryCase,
    attachments: transaction?.receiptAttachments ?? [],
    attachmentPreviewType: localReceiptEvidenceAttachmentPreviewType,
  })
  const isRecoveredReceiptEvidence =
    receiptRecoveryCase?.recoveredAttachmentId === receiptEvidenceAttachment?.id
  const canDeleteReceiptEvidence =
    receiptEvidenceAttachment !== undefined &&
    !receiptEvidenceAttachment.connectedPlatforms?.some(
      (connection) =>
        connection.platform === activeIntegration?.connectedPlatform &&
        connection.syncStatus === ConnectedPlatformStatus.Success,
    )
  const receiptEvidence = useMemo(() => {
    if (!transaction || !receiptEvidenceAttachment) return undefined

    const recoveredReceipt = isRecoveredReceiptEvidence
      ? receiptRecoveryCase?.recoveredReceipt
      : undefined

    return {
      receipt: {
        ...recoveredReceipt,
        submittedAt:
          recoveredReceipt?.submittedAt ?? receiptEvidenceAttachment.createdAt,
      },
      transaction: {
        amountCents: transaction.clearingOrAuthAmountCents,
        merchantName:
          transaction.normalizedMerchant?.normalizedMerchantName ??
          transaction.merchantName,
        date: transaction.clearingOrAuthDateMoment()?.format('YYYY-MM-DD'),
      },
      currency: transaction.currency,
    }
  }, [
    isRecoveredReceiptEvidence,
    receiptEvidenceAttachment,
    receiptRecoveryCase,
    transaction,
  ])
  const secondaryFlyoutContent =
    receiptAgentFlyoutEnabled && receiptEvidenceAttachment
      ? {
          panelTab: 'new' as const,
          className: 'ReceiptInspectionFlyout',
          hideCloseButton: true,
          alwaysOpen: true,
          renderContent: () => (
            <ReceiptEvidencePanel
              receiptRecoveryCase={
                isRecoveredReceiptEvidence ? receiptRecoveryCase : undefined
              }
              receiptAttachment={receiptEvidenceAttachment}
              receiptEvidence={receiptEvidence}
              onDelete={deleteAttachment}
              canDelete={canDeleteReceiptEvidence}
            />
          ),
        }
      : showExpensePolicyAIChat && aiPolicyAssistantEnabled && hasPolicyInsights
        ? {
            panelTab: 'new' as const,
            content: (
              <StreamExpensePolicyAgentChat
                className={'policyAgent'}
                context={{
                  organizationId: user?.organizationId,
                  transactionId: transactionId,
                }}
              />
            ),
          }
        : undefined

  const transactionSummary =
    transaction === undefined || cardUser === undefined ? null : (
      <TransactionSummarySection
        transaction={transaction}
        cardUser={cardUser}
        timezone={
          virtualCard?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }
        showPolicyInsights={!receiptAgentFlyoutEnabled}
      />
    )

  const transactionHeader =
    transaction === undefined || cardUser === undefined ? null : (
      <TransactionSummaryHeader transaction={transaction} />
    )

  const overviewTransactionSummary =
    transaction === undefined || cardUser === undefined ? null : (
      <TransactionSummarySection
        transaction={transaction}
        cardUser={cardUser}
        timezone={
          virtualCard?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }
        showHeader={false}
        showPolicyInsights={!receiptAgentFlyoutEnabled}
      />
    )

  const overviewContent =
    transaction === undefined || cardUser === undefined ? null : (
      <>
        {/* NOTE: Sections are surrounded by divs so that their styling
         * does not interfere with the separators.
         */}
        {receiptAgentFlyoutEnabled ? (
          <>
            <div>
              <ReceiptsSection
                transaction={transaction}
                receiptRecoveryCase={receiptRecoveryCase}
                isMockReceiptRecoveryPreview={isMockReceiptRecoveryPreview}
                attachments={transaction.receiptAttachments}
                uploadAttachments={dispatchUploadAttachments}
                deleteAttachment={deleteAttachment}
                uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
                uploadingAttachmentFileNames={uploadingAttachmentFileNames}
                failedUploadThumbnails={failedUploadThumbnails}
                failedUploadFileNames={failedUploadFileNames}
                recentlyUploadedAttachmentIds={recentlyUploadedAttachmentIds}
                topLevelError={attachmentsTopLevelError?.error}
                isReceiptRequired={
                  !!receiptManagementEnabled && transaction.receiptRequired
                }
                isReceiptSectionVisible={
                  user?.hasOrgBookkeeperPermissions &&
                  transaction.organizationReceiptRequired
                }
                receiptRule={orgReceiptRulesQuery.data?.receiptRule}
                patchTransaction={dispatchPatchTransaction}
              />
            </div>
            {shouldRenderReceiptAgentManagerReview({
              receiptAgentFlyoutEnabled,
              managerReviewRulesEnabled,
            }) && (
              <div>
                <ManagerReviewSection transaction={transaction} />
              </div>
            )}
            {hasDisplayablePolicyInsights &&
              showExpensePolicyAI &&
              aiPolicyAssistantEnabled && (
                <div>
                  <TransactionSummarySection
                    transaction={transaction}
                    cardUser={cardUser}
                    timezone={
                      virtualCard?.timezone ||
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    }
                    showHeader={false}
                    showTimeline={false}
                    showPolicyInsightsHeading
                  />
                </div>
              )}
          </>
        ) : (
          <>
            {managerReviewRulesEnabled && (
              <div>
                <ManagerReviewSection transaction={transaction} />
              </div>
            )}
            <div>
              <ReceiptsSection
                transaction={transaction}
                receiptRecoveryCase={null}
                attachments={transaction.receiptAttachments}
                uploadAttachments={dispatchUploadAttachments}
                deleteAttachment={deleteAttachment}
                uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
                uploadingAttachmentFileNames={uploadingAttachmentFileNames}
                failedUploadThumbnails={failedUploadThumbnails}
                failedUploadFileNames={failedUploadFileNames}
                recentlyUploadedAttachmentIds={recentlyUploadedAttachmentIds}
                topLevelError={attachmentsTopLevelError?.error}
                isReceiptRequired={
                  !!receiptManagementEnabled && transaction.receiptRequired
                }
                isReceiptSectionVisible={
                  user?.hasOrgBookkeeperPermissions &&
                  transaction.organizationReceiptRequired
                }
                receiptRule={orgReceiptRulesQuery.data?.receiptRule}
                patchTransaction={dispatchPatchTransaction}
              />
            </div>
          </>
        )}
        {expCatSchema && expCatSchema.length > 0 && (
          <div>
            <ExpenseDetailsSection
              transaction={transaction}
              splits={splitExpenseCategories}
              onChange={setSplitExpenseCategories}
              onSaveSplits={handleSaveSplitExpenseCategories}
              splitError={splitTransactionExpenseDataError?.error}
            />
          </div>
        )}
        <AdditionalFieldsSection transaction={transaction} />
        <div>
          <CardDetailsSection
            transaction={transaction}
            creditCardPermissions={creditCardPermissions}
            parentCreditCardPermissions={parentCreditCardPermissions}
            cardUser={cardUser}
            cardCreator={cardCreator}
          />
        </div>
      </>
    )

  const overviewContentWithSummary =
    transaction === undefined || cardUser === undefined ? null : (
      <>
        <div>{transactionSummary}</div>
        {overviewContent}
      </>
    )

  const overviewTabContent =
    transaction === undefined || cardUser === undefined ? null : (
      <>
        <div>{overviewTransactionSummary}</div>
        {overviewContent}
      </>
    )

  // Keeping the flyout as a page for now to restrict scope
  // When this is finally updated to not be a page, we will
  // need to update all sorts of urls
  // But also moving to BaseFlyout to make the eventual shift
  // as simple as possible

  return (
    <BaseFlyout
      className={'TransactionFlyout'}
      isOpen={true}
      onClose={() => dispatch(closeModal())}
      css={style}
      aria-label={translate('Transaction Details')}
      secondaryFlyoutContent={secondaryFlyoutContent}
    >
      {transaction === undefined || cardUser === undefined ? (
        <LoadingRightFlyoutContent />
      ) : transactionActivitiesEnabled ? (
        <TransactionDetailTabs
          activeTab={activeTab}
          activities={
            <TransactionActivitiesTab
              transactionId={transaction.id}
              enabled={activeTab === 'activities'}
              commentsEnabled={isTransactionConversationsEnabled(featureFlags)}
              merchantName={
                transaction.normalizedMerchant?.normalizedMerchantName ??
                transaction.merchantName ??
                translate(
                  transaction.isPending
                    ? 'Merchant Pending'
                    : 'Unknown Merchant',
                )
              }
            />
          }
          header={transactionHeader}
          overview={overviewTabContent}
          onTabChange={(nextTab) =>
            setTransactionDetailTabState({
              transactionId,
              activeTab: nextTab,
            })
          }
        />
      ) : (
        overviewContentWithSummary
      )}
    </BaseFlyout>
  )
}

export default TransactionDetail
